import clsx from 'clsx';
import type { ReactNode } from 'react';
import { Bar, BarChart, CartesianGrid, Cell, ComposedChart, Legend, Line, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { useI18n } from '../lib/i18n';
import { useMoney, useNumber } from './kit';
import { Table, Td, Th, Tr } from './ui';
import { useMonthName } from './a-common';
import { CoverageBadge, type TrendRow } from './a-finance';

/**
 * The charts of the Overview, Financial Intelligence and report pages. Each one has its figures beside it
 * as a table (behind "Show as a table") and a label that says what it draws, so nothing is only a picture.
 * Unknown values are gaps, never zero bars; months Finance could not reconcile are drawn faded and said so.
 */

/** Sentinel's categorical series — the design tokens' `chart` scale, as apps/sys's charts use it. */
export const SERIES = ['#C7102E', '#22D3EE', '#F9C751', '#7C5CFF', '#17B980', '#FF7A45', '#76777A', '#EE5468'] as const;

export const CHART = {
  revenue: SERIES[1],
  expenses: SERIES[0],
  result: SERIES[4],
  loss: SERIES[7],
  started: SERIES[4],
  cancelled: SERIES[0],
  current: SERIES[1],
  previous: SERIES[6],
} as const;

export const tooltipStyle = {
  background: 'var(--surface-raised)',
  border: '1px solid var(--line)',
  borderRadius: 10,
  color: 'var(--text-primary)',
  fontSize: 13,
};

const LOCALE_TAG = { en: 'en-GB', ar: 'ar-EG-u-nu-latn' } as const;

export function useCompact() {
  const { locale } = useI18n();
  return (v: number) => new Intl.NumberFormat(LOCALE_TAG[locale], { notation: 'compact', maximumFractionDigits: 1 }).format(v);
}

/** A chart with its label for assistive technology, an optional caption, and its figures as a table. */
export function ChartFrame({ label, caption, table, children, className }: { label: string; caption?: ReactNode; table: ReactNode; children: ReactNode; className?: string }) {
  const { t } = useI18n();
  return (
    <figure className="m-0 space-y-2">
      <div role="img" aria-label={label} className={clsx('h-64 w-full', className)} dir="ltr">
        {children}
      </div>
      {caption && <figcaption className="text-caption text-muted">{caption}</figcaption>}
      <details>
        <summary className="cursor-pointer select-none text-caption text-primary-600 dark:text-primary-400">{t('overview.chart.asTable')}</summary>
        <div className="mt-2">{table}</div>
      </details>
    </figure>
  );
}

/** Revenue (collected), expenses and the result by month, in the base currency — with gaps where unknown. */
export function MonthTrend({ rows, currency }: { rows: TrendRow[]; currency: string }) {
  const { t } = useI18n();
  const money = useMoney();
  const compact = useCompact();
  const monthName = useMonthName();
  if (!rows.length) return <p className="text-body-sm text-muted">{t('state.noData')}</p>;

  const data = rows.map((r) => ({ ...r, label: monthName(r.month, true) }));
  const unknown = rows.filter((r) => r.revenue === null || r.expenses === null || r.result === null).map((r) => monthName(r.month, true));
  const doubtful = rows.filter((r) => r.state !== 'COMPLETE').map((r) => monthName(r.month, true));
  const label = t('overview.chart.trendLabel', { from: monthName(rows[0].month), to: monthName(rows[rows.length - 1].month), currency });
  const cell = (v: number | null) => (v === null ? <span className="text-muted">{t('common.unknown')}</span> : money(v, currency));

  return (
    <ChartFrame
      label={label}
      caption={
        <span className="flex flex-col gap-0.5">
          {unknown.length > 0 && <span>{t('overview.chart.gaps', { months: unknown.join(' · ') })}</span>}
          {doubtful.length > 0 && <span>{t('overview.chart.faded', { months: doubtful.join(' · ') })}</span>}
        </span>
      }
      table={
        <Table>
          <thead>
            <tr>
              <Th>{t('finance.month')}</Th>
              <Th align="end">{t('finance.collected')}</Th>
              <Th align="end">{t('finance.expenses')}</Th>
              <Th align="end">{t('finance.result')}</Th>
              <Th>{t('finance.coverage')}</Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <Tr key={r.month}>
                <Td>{monthName(r.month)}</Td>
                <Td align="end" className="tabular">{cell(r.revenue)}</Td>
                <Td align="end" className="tabular">{cell(r.expenses)}</Td>
                <Td align="end" className="tabular">{cell(r.result)}</Td>
                <Td>
                  <span className="inline-flex flex-wrap items-center gap-1">
                    <CoverageBadge state={r.state} />
                    {r.partialMonth && <span className="text-caption text-muted">{t('finance.partialMonth')}</span>}
                  </span>
                </Td>
              </Tr>
            ))}
          </tbody>
        </Table>
      }
    >
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--line)" vertical={false} />
          <XAxis dataKey="label" stroke="var(--text-tertiary)" fontSize={12} />
          <YAxis stroke="var(--text-tertiary)" fontSize={12} width={64} tickFormatter={(v: number) => compact(v)} />
          <ReferenceLine y={0} stroke="var(--text-tertiary)" />
          <Tooltip
            contentStyle={tooltipStyle}
            formatter={(value: any, name: any) => [value === null || value === undefined ? t('common.unknown') : money(Number(value), currency), name]}
            labelFormatter={(label: any, payload: any) => {
              const row = payload?.[0]?.payload as TrendRow | undefined;
              return row && row.state !== 'COMPLETE' ? `${label} · ${t(`finance.coverage.${row.state}`)}` : label;
            }}
          />
          <Legend wrapperStyle={{ fontSize: 12 }} formatter={(v: any) => <span className="text-[var(--text-secondary)]">{v}</span>} />
          <Bar name={t('finance.collected')} dataKey="revenue" fill={CHART.revenue} radius={[4, 4, 0, 0]}>
            {data.map((d) => (
              <Cell key={d.month} fillOpacity={d.state === 'COMPLETE' ? 1 : 0.4} />
            ))}
          </Bar>
          <Bar name={t('finance.expenses')} dataKey="expenses" fill={CHART.expenses} radius={[4, 4, 0, 0]}>
            {data.map((d) => (
              <Cell key={d.month} fillOpacity={d.state === 'COMPLETE' ? 1 : 0.4} />
            ))}
          </Bar>
          <Line name={t('finance.result')} dataKey="result" stroke={CHART.result} strokeWidth={2} dot={{ r: 3 }} connectNulls={false} isAnimationActive={false} />
        </ComposedChart>
      </ResponsiveContainer>
    </ChartFrame>
  );
}

/** Subscriptions started and cancelled, month by month. */
export function StartedCancelled({ rows }: { rows: Array<{ month: string; started: number; cancelled: number }> }) {
  const { t } = useI18n();
  const num = useNumber();
  const monthName = useMonthName();
  if (!rows.length) return <p className="text-body-sm text-muted">{t('state.noData')}</p>;
  const data = rows.map((r) => ({ ...r, label: monthName(r.month, true) }));
  const started = rows.reduce((s, r) => s + r.started, 0);
  const cancelled = rows.reduce((s, r) => s + r.cancelled, 0);
  return (
    <ChartFrame
      className="h-48"
      label={t('finance.subs.trendLabel', { started, cancelled, months: rows.length })}
      caption={t('finance.subs.trendLabel', { started, cancelled, months: rows.length })}
      table={
        <Table>
          <thead>
            <tr>
              <Th>{t('finance.month')}</Th>
              <Th align="end">{t('finance.subs.started')}</Th>
              <Th align="end">{t('finance.subs.cancelled')}</Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <Tr key={r.month}>
                <Td>{monthName(r.month)}</Td>
                <Td align="end" className="tabular">{num(r.started)}</Td>
                <Td align="end" className="tabular">{num(r.cancelled)}</Td>
              </Tr>
            ))}
          </tbody>
        </Table>
      }
    >
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--line)" vertical={false} />
          <XAxis dataKey="label" stroke="var(--text-tertiary)" fontSize={12} />
          <YAxis stroke="var(--text-tertiary)" fontSize={12} width={40} allowDecimals={false} />
          <Tooltip contentStyle={tooltipStyle} />
          <Legend wrapperStyle={{ fontSize: 12 }} formatter={(v: any) => <span className="text-[var(--text-secondary)]">{v}</span>} />
          <Bar name={t('finance.subs.started')} dataKey="started" fill={CHART.started} radius={[4, 4, 0, 0]} />
          <Bar name={t('finance.subs.cancelled')} dataKey="cancelled" fill={CHART.cancelled} radius={[4, 4, 0, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </ChartFrame>
  );
}

/**
 * Named amounts as bars, with each amount written beside its bar — a chart that is its own table. A
 * value of null is said, never drawn as an empty bar.
 */
export function ValueBars({
  rows,
  format,
  tone = 'brand',
}: {
  rows: Array<{ key: string; label: ReactNode; value: number | null; display?: ReactNode; tone?: 'brand' | 'success' | 'warning' | 'error' | 'neutral' }>;
  format: (v: number) => ReactNode;
  tone?: 'brand' | 'success' | 'warning' | 'error' | 'neutral';
}) {
  const { t } = useI18n();
  const max = Math.max(1, ...rows.map((r) => Math.abs(r.value ?? 0)));
  const fills = { brand: 'bg-primary', success: 'bg-success', warning: 'bg-warning', error: 'bg-error-fill', neutral: 'bg-neutral-500' };
  return (
    <ul className="space-y-3">
      {rows.map((r) => (
        <li key={r.key}>
          <div className="mb-1 flex flex-wrap items-baseline justify-between gap-2 text-body-sm">
            <span className="min-w-0 text-[var(--text-secondary)]">{r.label}</span>
            <span className="tabular text-[var(--text-primary)]">{r.display ?? (r.value === null ? <span className="text-muted">{t('common.unknown')}</span> : format(r.value))}</span>
          </div>
          <div className="h-2 w-full overflow-hidden rounded-pill bg-[var(--surface-sunken)]" aria-hidden>
            {r.value !== null && <div className={clsx('h-full rounded-pill', fills[r.tone ?? tone])} style={{ width: `${(Math.abs(r.value) / max) * 100}%` }} />}
          </div>
        </li>
      ))}
    </ul>
  );
}

/** This period against the period before, as two bars per measure, with both figures written. */
export function CompareBars({ rows, currentLabel, previousLabel }: { rows: Array<{ key: string; label: ReactNode; now: number; before: number; change?: ReactNode }>; currentLabel: string; previousLabel: string }) {
  const num = useNumber();
  const max = Math.max(1, ...rows.flatMap((r) => [r.now, r.before]));
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-4 text-caption text-muted" aria-hidden>
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2 w-3 rounded-pill" style={{ background: CHART.current }} />
          {currentLabel}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2 w-3 rounded-pill" style={{ background: CHART.previous }} />
          {previousLabel}
        </span>
      </div>
      <ul className="space-y-3">
        {rows.map((r) => (
          <li key={r.key}>
            <div className="mb-1 flex flex-wrap items-baseline justify-between gap-2 text-body-sm">
              <span className="text-[var(--text-secondary)]">{r.label}</span>
              <span className="inline-flex flex-wrap items-baseline gap-2">
                <span className="tabular text-[var(--text-primary)]">
                  {num(r.now)} <span className="text-muted">/ {num(r.before)}</span>
                </span>
                {r.change}
              </span>
            </div>
            <div className="space-y-1" aria-hidden>
              <div className="h-2 w-full overflow-hidden rounded-pill bg-[var(--surface-sunken)]">
                <div className="h-full rounded-pill" style={{ width: `${(r.now / max) * 100}%`, background: CHART.current }} />
              </div>
              <div className="h-2 w-full overflow-hidden rounded-pill bg-[var(--surface-sunken)]">
                <div className="h-full rounded-pill" style={{ width: `${(r.before / max) * 100}%`, background: CHART.previous }} />
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
